import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import type { CrmLeadIntegrationChatTab } from '@/api/types/crm/crmLeadFunnels.types'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import { useCrmIntegrationChatUserAccess } from '@/hooks/useCrmIntegrationChatUserAccess'
import {
  IntegrationChatDockContext,
  IntegrationChatDockNotifyContext,
  type IntegrationChatDockContextValue,
  type IntegrationChatDockOpenOptions,
} from './integrationChatDockHooks'
import {
  getInitialIntegrationChatDockSession,
  writeIntegrationChatDockPersistedState,
  type IntegrationChatDockPersistedState,
  type IntegrationChatDockScreen,
} from './integrationChatDockPersistence'
import {
  EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD,
  fetchIntegrationChatGlobalUnread,
  type IntegrationChatGlobalUnreadSnapshot,
} from './integrationChatGlobalUnread'

type DockSessionSnapshot = IntegrationChatDockPersistedState

export const IntegrationChatDockProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { isLoading, providers } = useCrmIntegrationChatAvailability()
  const hasUserAccess = useCrmIntegrationChatUserAccess()
  const initialSession = useMemo(
    () => getInitialIntegrationChatDockSession(),
    [],
  )

  const [leadId, setLeadId] = useState<number | null>(initialSession.leadId)
  const [conversationId, setConversationId] = useState<number | null>(
    initialSession.conversationId,
  )
  const [communityTabs, setCommunityTabs] = useState<CrmLeadIntegrationChatTab[]>(
    [],
  )
  const [activeProvider, setActiveProvider] = useState<CrmChatProviderKey | null>(
    initialSession.activeProvider,
  )
  const [chatsListIntegrationId, setChatsListIntegrationId] = useState<
    number | null
  >(initialSession.chatsListIntegrationId)
  const [chatsListExternalChannelId, setChatsListExternalChannelId] = useState<
    string | null
  >(initialSession.chatsListExternalChannelId)
  const [expanded, setExpanded] = useState(initialSession.expanded)
  const [chatScreen, setChatScreenState] = useState<IntegrationChatDockScreen>(
    initialSession.screen,
  )
  const [launcherUnreadCount, setLauncherUnreadCount] = useState(0)
  const [launcherMyUnreadCount, setLauncherMyUnreadCount] = useState(0)
  const [providerUnreadCounts, setProviderUnreadCounts] = useState(
    EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD.byProvider,
  )
  const [providerMyUnreadCounts, setProviderMyUnreadCounts] = useState(
    EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD.myByProvider,
  )
  const [unreadByIntegrationId, setUnreadByIntegrationId] = useState(
    EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD.byIntegrationId,
  )
  const [myUnreadByIntegrationId, setMyUnreadByIntegrationId] = useState(
    EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD.myByIntegrationId,
  )

  const sessionCallbacksRef = useRef<{
    onClose?: () => void
    onMarkReadSuccess?: (leadId: number, conversationId?: number) => void
  }>({})

  const sessionRef = useRef<DockSessionSnapshot>(initialSession)
  sessionRef.current = {
    expanded,
    screen: chatScreen,
    activeProvider,
    chatsListIntegrationId,
    chatsListExternalChannelId,
    leadId,
    conversationId,
  }

  const persistSession = useCallback((patch?: Partial<DockSessionSnapshot>) => {
    const next = { ...sessionRef.current, ...patch }
    sessionRef.current = next
    writeIntegrationChatDockPersistedState(next)
  }, [])

  const applyGlobalUnread = useCallback(
    (snapshot: IntegrationChatGlobalUnreadSnapshot) => {
      setLauncherUnreadCount(snapshot.total)
      setLauncherMyUnreadCount(snapshot.myTotal)
      setProviderUnreadCounts(snapshot.byProvider)
      setProviderMyUnreadCounts(snapshot.myByProvider)
      setUnreadByIntegrationId(snapshot.byIntegrationId)
      setMyUnreadByIntegrationId(snapshot.myByIntegrationId)
    },
    [],
  )

  const applyLauncherUnread = useCallback(
    (count: number) => {
      const normalized =
        Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0
      applyGlobalUnread({
        ...EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD,
        total: normalized,
      })
    },
    [applyGlobalUnread],
  )

  const canFetchGlobalUnread =
    !isLoading &&
    (providers.vkontakte.canUseLeadChat ||
      providers.max.canUseLeadChat ||
      providers.telegram.canUseLeadChat ||
      providers.wazzup.canUseLeadChat) &&
    hasUserAccess

  useEffect(() => {
    if (!canFetchGlobalUnread) {
      applyGlobalUnread(EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD)
      return
    }

    let cancelled = false

    void (async () => {
      const snapshot = await fetchIntegrationChatGlobalUnread()
      if (!cancelled && snapshot != null) {
        applyGlobalUnread(snapshot)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [applyGlobalUnread, canFetchGlobalUnread])

  const clearDockSession = useCallback(() => {
    sessionCallbacksRef.current = {}
    setLeadId(null)
    setConversationId(null)
    setCommunityTabs([])
    setActiveProvider(null)
    setChatsListIntegrationId(null)
    setChatsListExternalChannelId(null)
    setExpanded(false)
    setChatScreenState('integrations-picker')
    writeIntegrationChatDockPersistedState(null)
  }, [])

  const notifyMarkReadSuccess = useCallback(
    (targetLeadId: number, targetConversationId?: number) => {
      sessionCallbacksRef.current.onMarkReadSuccess?.(
        targetLeadId,
        targetConversationId,
      )
    },
    [],
  )

  const openDock = useCallback(
    (targetLeadId: number, options?: IntegrationChatDockOpenOptions) => {
      sessionCallbacksRef.current = {
        onClose: options?.onClose,
        onMarkReadSuccess: options?.onMarkReadSuccess,
      }

      const nextConversationId =
        options?.conversationId != null &&
        Number.isFinite(Number(options.conversationId))
          ? Number(options.conversationId)
          : null

      setLeadId(targetLeadId)
      setConversationId(nextConversationId)
      setCommunityTabs(options?.communityTabs ?? [])
      setActiveProvider(options?.provider ?? 'vkontakte')
      if ((options?.provider ?? 'vkontakte') !== 'wazzup') {
        setChatsListExternalChannelId(null)
      }
      setExpanded(true)
      setChatScreenState('conversation')

      persistSession({
        leadId: targetLeadId,
        conversationId: nextConversationId,
        activeProvider: options?.provider ?? 'vkontakte',
        chatsListExternalChannelId:
          (options?.provider ?? 'vkontakte') === 'wazzup'
            ? sessionRef.current.chatsListExternalChannelId
            : null,
        expanded: true,
        screen: 'conversation',
      })
    },
    [persistSession],
  )

  const switchDockLead = useCallback(
    (
      targetLeadId: number,
      options?: {
        conversationId?: number | null
        provider?: CrmChatProviderKey
      },
    ) => {
      const nextConversationId =
        options?.conversationId != null &&
        Number.isFinite(Number(options.conversationId))
          ? Number(options.conversationId)
          : null
      const nextProvider = options?.provider ?? activeProvider

      setLeadId(targetLeadId)
      setConversationId(nextConversationId)
      setCommunityTabs([])
      if (options?.provider) {
        setActiveProvider(options.provider)
      }
      if (nextProvider !== 'wazzup') {
        setChatsListExternalChannelId(null)
      }
      setChatScreenState('conversation')

      persistSession({
        leadId: targetLeadId,
        conversationId: nextConversationId,
        activeProvider: nextProvider,
        chatsListExternalChannelId:
          nextProvider === 'wazzup'
            ? sessionRef.current.chatsListExternalChannelId
            : null,
        screen: 'conversation',
      })
    },
    [activeProvider, persistSession],
  )

  const setChatScreen = useCallback(
    (screen: IntegrationChatDockScreen) => {
      setChatScreenState(screen)
      persistSession({ screen })
    },
    [persistSession],
  )

  const backToIntegrationsPicker = useCallback(() => {
    setLeadId(null)
    setConversationId(null)
    setCommunityTabs([])
    setActiveProvider(null)
    setChatsListIntegrationId(null)
    setChatsListExternalChannelId(null)
    setChatScreenState('integrations-picker')

    persistSession({
      leadId: null,
      conversationId: null,
      activeProvider: null,
      chatsListIntegrationId: null,
      chatsListExternalChannelId: null,
      screen: 'integrations-picker',
    })
  }, [persistSession])

  const backToProviderChatsList = useCallback(() => {
    setLeadId(null)
    setConversationId(null)
    setCommunityTabs([])
    setActiveProvider((prev) => prev ?? 'vkontakte')
    setChatScreenState('chats-list')

    persistSession({
      leadId: null,
      conversationId: null,
      screen: 'chats-list',
      activeProvider: activeProvider ?? 'vkontakte',
      chatsListIntegrationId,
      chatsListExternalChannelId,
    })
  }, [
    activeProvider,
    chatsListExternalChannelId,
    chatsListIntegrationId,
    persistSession,
  ])

  const openProviderChats = useCallback(
    (provider: CrmChatProviderKey) => {
      // Wazzup: аккаунты на integrations-picker, не отдельный экран.
      if (provider === 'wazzup') {
        setActiveProvider(null)
        setLeadId(null)
        setConversationId(null)
        setCommunityTabs([])
        setChatsListIntegrationId(null)
        setChatsListExternalChannelId(null)
        setChatScreenState('integrations-picker')
        setExpanded(true)
        persistSession({
          activeProvider: null,
          leadId: null,
          conversationId: null,
          chatsListIntegrationId: null,
          chatsListExternalChannelId: null,
          screen: 'integrations-picker',
          expanded: true,
        })
        return
      }

      setActiveProvider(provider)
      setLeadId(null)
      setConversationId(null)
      setCommunityTabs([])
      setChatsListIntegrationId(null)
      setChatsListExternalChannelId(null)
      setChatScreenState('chats-list')
      setExpanded(true)

      persistSession({
        activeProvider: provider,
        leadId: null,
        conversationId: null,
        chatsListIntegrationId: null,
        chatsListExternalChannelId: null,
        screen: 'chats-list',
        expanded: true,
      })
    },
    [persistSession],
  )

  const openWazzupAccountChannels = useCallback(
    (integrationId: number) => {
      const nextId =
        Number.isFinite(integrationId) && integrationId > 0
          ? Math.floor(integrationId)
          : null
      if (nextId == null) {
        return
      }

      setActiveProvider('wazzup')
      setLeadId(null)
      setConversationId(null)
      setCommunityTabs([])
      setChatsListIntegrationId(nextId)
      setChatsListExternalChannelId(null)
      setChatScreenState('wazzup-channels')
      setExpanded(true)

      persistSession({
        activeProvider: 'wazzup',
        leadId: null,
        conversationId: null,
        chatsListIntegrationId: nextId,
        chatsListExternalChannelId: null,
        screen: 'wazzup-channels',
        expanded: true,
      })
    },
    [persistSession],
  )

  const openWazzupChannelChats = useCallback(
    (integrationId: number, externalChannelId: string) => {
      const nextId =
        Number.isFinite(integrationId) && integrationId > 0
          ? Math.floor(integrationId)
          : null
      const nextChannelId = externalChannelId.trim()
      if (nextId == null || !nextChannelId) {
        return
      }

      setActiveProvider('wazzup')
      setLeadId(null)
      setConversationId(null)
      setCommunityTabs([])
      setChatsListIntegrationId(nextId)
      setChatsListExternalChannelId(nextChannelId)
      setChatScreenState('chats-list')
      setExpanded(true)

      persistSession({
        activeProvider: 'wazzup',
        leadId: null,
        conversationId: null,
        chatsListIntegrationId: nextId,
        chatsListExternalChannelId: nextChannelId,
        screen: 'chats-list',
        expanded: true,
      })
    },
    [persistSession],
  )

  /** Назад с каналов Wazzup — к плиткам интеграций (аккаунты там). */
  const backToWazzupAccounts = useCallback(() => {
    setLeadId(null)
    setConversationId(null)
    setCommunityTabs([])
    setActiveProvider(null)
    setChatsListIntegrationId(null)
    setChatsListExternalChannelId(null)
    setChatScreenState('integrations-picker')

    persistSession({
      leadId: null,
      conversationId: null,
      activeProvider: null,
      chatsListIntegrationId: null,
      chatsListExternalChannelId: null,
      screen: 'integrations-picker',
    })
  }, [persistSession])

  const backToWazzupChannels = useCallback(() => {
    setLeadId(null)
    setConversationId(null)
    setCommunityTabs([])
    setActiveProvider('wazzup')
    setChatsListExternalChannelId(null)
    setChatScreenState('wazzup-channels')

    persistSession({
      leadId: null,
      conversationId: null,
      activeProvider: 'wazzup',
      chatsListIntegrationId,
      chatsListExternalChannelId: null,
      screen: 'wazzup-channels',
    })
  }, [chatsListIntegrationId, persistSession])

  const closeDock = useCallback(() => {
    const onClose = sessionCallbacksRef.current.onClose
    clearDockSession()
    onClose?.()
  }, [clearDockSession])

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev
      persistSession({ expanded: next })
      return next
    })
  }, [persistSession])

  const toggleLauncher = useCallback(() => {
    if (expanded) {
      toggleExpanded()
      return
    }

    setExpanded(true)
    persistSession({ expanded: true })
  }, [expanded, persistSession, toggleExpanded])

  const setDockConversationId = useCallback(
    (id: number | null) => {
      const nextId =
        id != null && Number.isFinite(Number(id)) ? Number(id) : null
      setConversationId(nextId)
      persistSession({ conversationId: nextId })
    },
    [persistSession],
  )

  const setDockChatsListIntegrationId = useCallback(
    (integrationId: number | null) => {
      const nextId =
        integrationId != null && Number.isFinite(Number(integrationId))
          ? Math.floor(Number(integrationId))
          : null
      setChatsListIntegrationId(nextId)
      persistSession({ chatsListIntegrationId: nextId })
    },
    [persistSession],
  )

  const value = useMemo(
    (): IntegrationChatDockContextValue => ({
      leadId,
      conversationId,
      communityTabs,
      activeProvider,
      chatsListIntegrationId,
      chatsListExternalChannelId,
      expanded,
      chatScreen,
      launcherUnreadCount,
      launcherMyUnreadCount,
      providerUnreadCounts,
      providerMyUnreadCounts,
      unreadByIntegrationId,
      myUnreadByIntegrationId,
      openDock,
      switchDockLead,
      closeDock,
      toggleExpanded,
      toggleLauncher,
      setChatScreen,
      openProviderChats,
      openWazzupAccountChannels,
      openWazzupChannelChats,
      backToIntegrationsPicker,
      backToWazzupAccounts,
      backToWazzupChannels,
      backToProviderChatsList,
      setConversationId: setDockConversationId,
      setChatsListIntegrationId: setDockChatsListIntegrationId,
      applyGlobalUnread,
      applyLauncherUnread,
    }),
    [
      activeProvider,
      applyGlobalUnread,
      applyLauncherUnread,
      backToIntegrationsPicker,
      backToProviderChatsList,
      backToWazzupAccounts,
      backToWazzupChannels,
      chatScreen,
      chatsListExternalChannelId,
      chatsListIntegrationId,
      closeDock,
      communityTabs,
      conversationId,
      expanded,
      launcherUnreadCount,
      launcherMyUnreadCount,
      providerUnreadCounts,
      providerMyUnreadCounts,
      unreadByIntegrationId,
      myUnreadByIntegrationId,
      leadId,
      openDock,
      openProviderChats,
      openWazzupAccountChannels,
      openWazzupChannelChats,
      setChatScreen,
      setDockChatsListIntegrationId,
      setDockConversationId,
      switchDockLead,
      toggleExpanded,
      toggleLauncher,
    ],
  )

  const notifyValue = useMemo(
    () => ({ notifyMarkReadSuccess }),
    [notifyMarkReadSuccess],
  )

  return (
    <IntegrationChatDockContext.Provider value={value}>
      <IntegrationChatDockNotifyContext.Provider value={notifyValue}>
        {children}
      </IntegrationChatDockNotifyContext.Provider>
    </IntegrationChatDockContext.Provider>
  )
}
